import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, throwError, from, forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { Item } from './item.model';
import { Page } from '../../models/page-wrapper.model';
import { environment } from '../../../environments/environment';
import { PickerRecentCacheService } from '../../shared/services/picker-recent-cache.service';
import { EntityDropdownPickerAdapter } from '../../shared/components/entity-dropdown-picker/entity-dropdown-picker.model';
import { PickerRecentRecord } from '../../shared/components/entity-dropdown-picker/picker-recent.model';

@Injectable({
  providedIn: 'root'
})
export class ItemService {
  private apiUrl = `${environment.restApiUrl}masterdata/items`;
  private readonly pickerEntityKey = 'item';

  constructor(
    private http: HttpClient,
    private recentPickerCache: PickerRecentCacheService
  ) { }

  // Get all items with pagination
  getAllItems(params?: any): Observable<Page<Item>> {
    return this.http.get<Page<Item>>(this.apiUrl, { params }).pipe(
      catchError(this.handleError)
    );
  }

  searchItems(query: string, page = 0, size = 25): Observable<Page<Item>> {
    return forkJoin([
      this.searchItemsByField('erpItemNumber', query, page, size),
      this.searchItemsByField('erpItemNameE', query, page, size),
    ]).pipe(
      map(([numberPage, namePage]) => this.mergeItemPages(numberPage, namePage, page, size)),
      catchError(this.handleError)
    );
  }

  // Get item by ID
  getItemById(id: number): Observable<Item> {
    return this.http.get<Item>(`${this.apiUrl}/${id}`).pipe(
      catchError(this.handleError)
    );
  }

  loadRecentItems(limit = 10): Observable<Item[]> {
    return from(this.recentPickerCache.loadRecent<Item>(this.pickerEntityKey, limit));
  }

  getFreshItemById(id: number | string): Observable<Item> {
    return this.getItemById(Number(id));
  }

  rememberItem(item: Item): Observable<void> {
    const record: PickerRecentRecord<Item> = {
      key: `${this.pickerEntityKey}::${item.itemId}`,
      entityKey: this.pickerEntityKey,
      userKey: this.getCurrentUserKey(),
      entityId: item.itemId,
      label: `${item.erpItemNumber ?? ''} - ${item.erpItemNameE ?? ''}`.trim(),
      payload: item,
      lastUsedAt: Date.now(),
    };

    return from(this.recentPickerCache.remember(record));
  }

  createItemPickerAdapter(): EntityDropdownPickerAdapter<Item> {
    return {
      entityKey: this.pickerEntityKey,
      loadRecent: (limit?: number) => this.loadRecentItems(limit),
      search: (query: string, page: number, size: number) => this.searchItems(query, page, size),
      getFreshById: (id: number | string) => this.getFreshItemById(id),
      rememberSelection: (item: Item) => this.rememberItem(item),
      getId: (item: Item) => item.itemId,
      getLabel: (item: Item) => `${item.erpItemNumber ?? ''} - ${item.erpItemNameE ?? ''}`.trim(),
      getSecondaryLabel: () => '',
      filterBy: 'erpItemNumber,erpItemNameE',
    };
  }

  // Create new item
  createItem(item: Item): Observable<Item> {
    return this.http.post<Item>(this.apiUrl, item).pipe(
      catchError(this.handleError)
    );
  }

  // Update existing item
  updateItem(item: Item): Observable<Item> {
    return this.http.put<Item>(`${this.apiUrl}/${item.itemId}`, item).pipe(
      catchError(this.handleError)
    );
  }

  // Delete item
  deleteItem(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${id}`).pipe(
      catchError(this.handleError)
    );
  }

  private handleError(error: any): Observable<never> {
    console.error('An error occurred in ItemsService:', error);
    return throwError(() => new Error('Something bad happened; please try again later.'));
  }

  private getCurrentUserKey(): string {
    try {
      const rawUser = sessionStorage.getItem('appUser');
      if (!rawUser) {
        return 'anonymous';
      }

      const parsed = JSON.parse(rawUser);
      return String(parsed?.id ?? 'anonymous');
    } catch {
      return 'anonymous';
    }
  }

  private searchItemsByField(
    field: 'erpItemNumber' | 'erpItemNameE',
    query: string,
    page: number,
    size: number
  ): Observable<Page<Item>> {
    const params = {
      [field]: query,
      page,
      size,
    };

    return this.http.get<Page<Item>>(this.apiUrl, { params }).pipe(
      catchError(() => of(this.emptyItemPage(page, size)))
    );
  }

  private mergeItemPages(
    firstPage: Page<Item>,
    secondPage: Page<Item>,
    page: number,
    size: number
  ): Page<Item> {
    const merged = new Map<number, Item>();
    [...(firstPage.content || []), ...(secondPage.content || [])].forEach((item) => {
      if (item?.itemId !== undefined && item?.itemId !== null) {
        merged.set(item.itemId, item);
      }
    });

    const content = Array.from(merged.values()).slice(0, size);
    return {
      ...firstPage,
      content,
      number: page,
      size,
      totalElements: merged.size,
      numberOfElements: content.length,
    };
  }

  private emptyItemPage(page: number, size: number): Page<Item> {
    return {
      content: [],
      pageable: {
        pageNumber: page,
        pageSize: size,
        sort: { sorted: false, unsorted: true, empty: true },
        offset: page * size,
        paged: true,
        unpaged: false,
      },
      last: true,
      totalPages: 0,
      totalElements: 0,
      size,
      number: page,
      sort: { sorted: false, unsorted: true, empty: true },
      first: page === 0,
      numberOfElements: 0,
      empty: true,
    };
  }
}
